import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    MenuItem,
    Chip,
    Tooltip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { currencyFormatter } from '@utils/Numbers';
import BranchOfficeInventoryService from '@api/BranchOfficeInventories/BranchOfficeInventoryCtrl';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import {
    BranchOfficeInventory,
    describeAvailablePresentations,
    describePresentation,
    formatQuantity,
    getAvailableUnits
} from '@api/BranchOfficeInventories/BranchOfficeInventory';
import { ModalConfirmCount } from '@screens/BranchOffices/Inventory/ModalConfirmCount';
import { ModalRegisterShrinkage } from '@screens/BranchOffices/Inventory/ModalRegisterShrinkage';
import InventoryServ from '@api/Inventory/InventoryServ';
import InventoryUsageServ, { InventoryUsage } from '@api/Inventory/InventoryUsage';
import {
    describeUsageWarmup,
    getStockDays,
    getStockStatusByDays,
    MIN_DAYS_WITH_SALES,
    StockDays
} from '@api/Inputs/StockStatus';
import { usePermissionSubject } from '@hooks/usePermissionSubject';
import { can, Capability } from '@utils/Permissions/Capabilities';
import useOpenModal from '@hooks/useOpenModal';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';

interface ListInventoryProps {
    branchOfficeId: string;
    /** Umbrales en días de ESTA sucursal (`branch_offices.stockDays`); ausente = default. */
    stockDays?: Partial<StockDays>;
    onNewOrder: () => void;
}

const ListInventory: React.FunctionComponent<ListInventoryProps> = (props) => {
    const { branchOfficeId, onNewOrder } = props;
    const thresholds = useMemo(() => getStockDays({ stockDays: props.stockDays }), [props.stockDays]);
    const branchOfficeInventoryService = useMemo(() => new BranchOfficeInventoryService(), []);
    // Compra y Venta (costos del catálogo) solo caben, y solo importan, en pantalla ancha
    const wide = useMediaQuery(useTheme().breakpoints.up('lg'));
    // Quién contó queda registrado en el movimiento del ledger
    const { loggedUser } = useSelector((state: RootReducer) => state.auth);
    const [inventory, setInventory] = useState<BranchOfficeInventory[]>([]);
    const modalConfirmCount = useOpenModal();
    // Merma (plan de inventario F2): el botón se pinta con la capacidad; el gate va en
    // `InventoryServ.registerShrinkage` y el candado real es el PIN del gerente
    const modalShrinkage = useOpenModal();
    const subject = usePermissionSubject();
    const canRegisterShrinkage = can(subject, Capability.InventoryShrinkage, { branchOfficeId });
    const [filters, setFilters] = useState({ search: '' });
    // MODO CONTEO (2026-08-19, pedido del negocio): capturar sobre la MISMA lista en
    // vez de ir agregando insumo por insumo en un modal — con 107 insumos eso era
    // impracticable. Lo capturado vive aquí hasta que se autoriza y se guarda.
    const [countMode, setCountMode] = useState(false);
    // Orden EXPLÍCITO y estable. Antes no había ninguno: `getInventory` devolvía
    // primero los docs que la sucursal ya tenía (en el orden arbitrario de
    // Firestore) y luego el resto del catálogo, así que la tabla se reacomodaba
    // sola al cambiar de sucursal — y otra vez al entrar al conteo, porque ese
    // modo ordenaba distinto. Ahora el orden es el MISMO en los dos modos.
    // Default "lo que se acaba primero" (decisión del negocio, 13-oct); "Categoría" sigue
    // para contar en bodega
    const [orderBy, setOrderBy] = useState<'runningOut' | 'category' | 'name' | 'usage'>('runningOut');
    const [counted, setCounted] = useState<Record<string, string>>({});
    // El ritmo diario de la sucursal (plan de inventario F4): UN documento que escribe
    // el aseo. De aquí salen los días de inventario y el orden "los que más se usan".
    // Hasta F4 ese orden leía el ledger ENTERO de la sucursal en cada carga.
    const [usage, setUsage] = useState<InventoryUsage | undefined>(undefined);
    const usageReady = !!usage && usage.daysWithSales >= MIN_DAYS_WITH_SALES;

    /** Unidades base por día de este insumo en esta sucursal; `undefined` = sin ritmo. */
    const perDayOf = (row: BranchOfficeInventory): number | undefined =>
        usageReady && row.input?.id ? usage?.perDay?.[row.input.id] : undefined;
    const statusOf = (row: BranchOfficeInventory) =>
        getStockStatusByDays(getAvailableUnits(row), perDayOf(row), row.input?.unit?.unit || '', thresholds);

    /** 1 lectura. Sin el doc (el aseo no ha corrido para esta sucursal) todo queda "—". */
    const loadUsage = async () => {
        try {
            setUsage(await InventoryUsageServ.getUsage(branchOfficeId));
        } catch {
            setUsage(undefined);
        }
    };

    /** Lo capturado, ya normalizado (los vacíos NO se tocan). */
    const countedEntries = useMemo(
        () =>
            Object.entries(counted)
                .filter(([, value]) => String(value).trim() !== '')
                .map(([inputId, value]) => ({ inputId, countedQuantity: Number(value) || 0 })),
        [counted]
    );

    const exitCountMode = () => {
        setCountMode(false);
        setCounted({});
    };

    const saveCount = async () => {
        showLoader();
        try {
            const result = await InventoryServ.registerCount(branchOfficeId, countedEntries, {
                userId: loggedUser?.uid,
                note: 'Conteo desde el inventario de sucursal'
            });
            const faltante = Math.abs(result.totalShrinkage);
            showAlertMessage(
                result.discrepancies === 0
                    ? `Conteo guardado: los ${countedEntries.length} insumos cuadraron`
                    : `Conteo guardado: ${result.discrepancies} con diferencia${
                          faltante ? ` · faltante ${faltante} unidades base` : ''
                      }`
            );
            exitCountMode();
            await getData();
        } catch (error: any) {
            showAlertError(error?.response?.data?.message || error);
        }
        hideLoader();
    };

    const getData = async () => {
        showLoader();
        try {
            void loadUsage();
            const data = await branchOfficeInventoryService.getInventory(branchOfficeId, true);
            setInventory(data);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, [branchOfficeId]);

    // ------------------------------------------------------------------------
    // Columnas (F0b, 2026-10-13, fotos de Av. Juárez): ocho columnas con el mismo
    // `flex` dejaban el nombre en "Concentrado de" / "Contenedor grar" — la sucursal
    // "adivinaba el insumo" — y "Disponible" y "Estado" fuera de la pantalla, a la
    // derecha. En medio, "Unidad Actual" enseñaba 2,602 ml de una presentación de
    // 1,800 (contador viejo derivado con otro rendimiento). Queda lo que la
    // operación necesita, en este orden: Insumo (ancho, con salto de línea) ·
    // Presentación ("1.8 L = 1,800 ml") · Disponible (lo real) · Estado · Contado.
    // "Stock" y "Unidad Actual" se van: `totalUnits` es la verdad. Compra y Venta
    // son del catálogo, no de la operación: solo en pantallas anchas (`wide`).
    // ------------------------------------------------------------------------
    const columns = useMemo<ResponsiveGridColDef<BranchOfficeInventory>[]>(
        () => [
            {
                field: 'name',
                flex: 3,
                minWidth: 200,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Insumo
                    </Typography>
                ),
                // El nombre COMPLETO aunque ocupe dos líneas (`getRowHeight 'auto'` abajo)
                renderCell: ({ row }) => (
                    <Typography sx={{ whiteSpace: 'normal', lineHeight: 1.25 }}>
                        {row.input?.name || '(insumo borrado)'}
                    </Typography>
                ),
                valueGetter: ({ row }) => row.input?.name || ''
            },
            {
                field: 'presentation',
                flex: 1.4,
                minWidth: 140,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Presentación
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Typography sx={{ whiteSpace: 'normal', lineHeight: 1.25 }}>{describePresentation(row.input)}</Typography>
                ),
                valueGetter: ({ row }) => describePresentation(row.input)
            },
            {
                field: 'purchaseCost',
                flex: 1,
                minWidth: 90,
                showInMobile: false,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Compra
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{currencyFormatter(row.input?.purchaseCost)}</Typography>,
                valueGetter: ({ row }) => row.input?.purchaseCost ?? 0
            },
            {
                field: 'costOfSale',
                flex: 1,
                minWidth: 90,
                showInMobile: false,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Venta
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{currencyFormatter(row.input?.costOfSale)}</Typography>,
                valueGetter: ({ row }) => row.input?.costOfSale ?? 0
            },
            {
                // Saldo REAL en unidades base (F5). `stock`/`currentUnit` son
                // derivados suyos: el contador de dos niveles se equivocaba al
                // consumir más de una presentación de golpe. En NEGATIVO se pinta
                // rojo — es información, nunca un candado para vender (§4.2).
                field: 'totalUnits',
                flex: 1.4,
                minWidth: 130,
                showInMobile: true,
                sortable: true,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Disponible
                    </Typography>
                ),
                renderCell: ({ row }) => {
                    const total = getAvailableUnits(row);
                    const presentations = describeAvailablePresentations(row);
                    return (
                        <Stack>
                            <Typography fontWeight={600} color={total < 0 ? 'error.main' : 'text.primary'}>
                                {`${formatQuantity(total)} ${row.input?.unit?.unit || ''}`}
                            </Typography>
                            {presentations && (
                                <Typography variant="caption" color="text.secondary">
                                    {presentations}
                                </Typography>
                            )}
                        </Stack>
                    );
                },
                valueGetter: ({ row }) => getAvailableUnits(row)
            },
            {
                // Días de inventario (F4): lo disponible ÷ lo que ESTA sucursal gasta al
                // día. "—" sin ritmo (no sale por receta, o la sucursal no junta 3 días).
                field: 'days',
                flex: 0.8,
                minWidth: 80,
                showInMobile: true,
                sortable: true,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Días
                    </Typography>
                ),
                renderCell: ({ row }) => {
                    const status = statusOf(row);
                    return (
                        <Typography fontWeight={600} color={status.days !== null && status.days < thresholds.critical ? 'error.main' : 'text.primary'}>
                            {status.days === null ? '—' : formatQuantity(status.days, 1)}
                        </Typography>
                    );
                },
                valueGetter: ({ row }) => statusOf(row).days ?? Number.POSITIVE_INFINITY
            },
            {
                field: 'status',
                flex: 1,
                minWidth: 110,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Estado
                    </Typography>
                ),
                // En DÍAS al ritmo de esta sucursal (plan de inventario F4), con los
                // umbrales de la sucursal. Hasta F4 comparaba presentaciones contra los
                // umbrales del catálogo (iguales para todas): 803 de 837 renglones salían
                // "Crítico". El tooltip dice el porqué.
                renderCell: ({ row }) => {
                    const status = statusOf(row);
                    return (
                        <Tooltip title={status.reason}>
                            <Stack>
                                <Chip
                                    label={status.label}
                                    color={status.color}
                                    variant={status.label === '—' ? 'outlined' : 'filled'}
                                    size="small"
                                />
                            </Stack>
                        </Tooltip>
                    );
                }
            },
            ...(countMode
                ? [
                      {
                          // Captura en la MISMA fila: con 107 insumos, un modal que
                          // los agrega de uno en uno era impracticable. En blanco =
                          // no se cuenta ese insumo (no se toca su saldo)
                          field: 'counted',
                          flex: 1,
                          showInMobile: true,
                          sortable: false,
                          disableColumnMenu: true,
                          renderHeader: () => (
                              <Typography color="primary" fontWeight={700}>
                                  Contado
                              </Typography>
                          ),
                          renderCell: ({ row }: any) => (
                              <TextField
                                  size="small"
                                  type="number"
                                  placeholder="—"
                                  value={counted[row.input?.id] ?? ''}
                                  onChange={(e) =>
                                      setCounted((prev) => ({ ...prev, [row.input?.id]: e.target.value }))
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ width: 120 }}
                                  InputProps={{
                                      endAdornment: (
                                          <Typography variant="caption" color="text.secondary">
                                              {row.input?.unit?.unit}
                                          </Typography>
                                      )
                                  }}
                              />
                          )
                      } as ResponsiveGridColDef
                  ]
                : [])
        ],
        // `statusOf`/`perDayOf` cambian con `usage` y `thresholds`: son las deps reales
        // eslint-disable-next-line
        [countMode, counted, usage, thresholds]
    );

    /**
     * Orden de la tabla — el MISMO en modo normal y en conteo, para que entrar a
     * contar no reacomode las filas bajo la mano de quien está capturando.
     *   · se acaba primero → días de inventario ascendente, los "—" al final (default,
     *                        decisión del negocio)
     *   · categoría        → lo que vive junto en la bodega se cuenta junto
     *   · nombre           → para buscar uno específico
     *   · más usado        → ritmo diario (`inventory-usage`); las primeras filas
     *                        concentran casi todo el dinero (medido: 5 insumos = 67%)
     */
    const orderedInventory = useMemo(() => {
        const byName = (a: BranchOfficeInventory, b: BranchOfficeInventory) =>
            (a.input?.name || '').localeCompare(b.input?.name || '', 'es');
        const valueOf = (item: BranchOfficeInventory) => getAvailableUnits(item) * (Number(item.input?.unitCost) || 0);
        const daysOf = (item: BranchOfficeInventory) => statusOf(item).days ?? Number.POSITIVE_INFINITY;
        return [...inventory].sort((a, b) => {
            if (orderBy === 'runningOut') {
                const daysDiff = daysOf(a) - daysOf(b);
                if (daysDiff !== 0 && Number.isFinite(daysDiff)) return daysDiff;
                return byName(a, b);
            }
            if (orderBy === 'usage') {
                const usageDiff = (perDayOf(b) || 0) - (perDayOf(a) || 0);
                if (usageDiff !== 0) return usageDiff;
                const valueDiff = valueOf(b) - valueOf(a);
                if (valueDiff !== 0) return valueDiff;
                return byName(a, b);
            }
            if (orderBy === 'category') {
                const catDiff = (a.input?.inputCategoryId || '').localeCompare(b.input?.inputCategoryId || '');
                if (catDiff !== 0) return catDiff;
            }
            return byName(a, b);
        });
        // eslint-disable-next-line
    }, [inventory, orderBy, usage, thresholds]);

    return (
        <Stack height="100%" spacing={2}>
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Ordenar por"
                            value={orderBy}
                            onChange={(e) => setOrderBy(e.target.value as 'runningOut' | 'category' | 'name' | 'usage')}
                        >
                            <MenuItem value="runningOut">Lo que se acaba primero</MenuItem>
                            <MenuItem value="category">Categoría (para contar en bodega)</MenuItem>
                            <MenuItem value="name">Nombre</MenuItem>
                            <MenuItem value="usage">Los que más se usan</MenuItem>
                        </TextField>
                        {!usageReady && (
                            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                {describeUsageWarmup(usage?.daysWithSales)}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} md={3} textAlign="right">
                        {/* Conteo físico: se captura sobre la MISMA lista (F5 §4.2).
                            Es el paso que habilita encender el descuento en esta
                            sucursal y el checkpoint del rebuild */}
                        {countMode ? (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button color="inherit" onClick={exitCountMode}>
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={countedEntries.length === 0}
                                    onClick={modalConfirmCount.open}
                                >
                                    {`Guardar (${countedEntries.length})`}
                                </Button>
                            </Stack>
                        ) : (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                {canRegisterShrinkage && (
                                    <Button variant="outlined" color="primary" onClick={modalShrinkage.open}>
                                        Registrar merma
                                    </Button>
                                )}
                                <Button variant="outlined" color="primary" onClick={() => setCountMode(true)}>
                                    Conteo físico
                                </Button>
                            </Stack>
                        )}
                    </Grid>
                    <Grid item xs={12} md={3} textAlign="right">
                        {!countMode && (
                            <Button variant="contained" color="primary" onClick={onNewOrder} endIcon={<AddIcon />}>
                                Nuevo pedido
                            </Button>
                        )}
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Buscar insumo"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
            <ResponsiveDataGrid
                searchValue={filters.search}
                getRowId={(row) => `${row.id}-${row.input.id}`}
                rows={orderedInventory}
                columns={columns}
                // El nombre completo del insumo aunque ocupe dos líneas (F0b)
                getRowHeight={() => 'auto'}
                columnVisibilityModel={{ purchaseCost: wide, costOfSale: wide }}
                sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
            />
            {/* El menú de acciones se quitó (2026-08-19, pedido del negocio): desde
                aquí NO se editan insumos ni se toca el stock a mano.
                  · El insumo es del CATÁLOGO GLOBAL — editarlo desde una sucursal
                    cambiaba el insumo para todas.
                  · El stock solo se mueve por hechos: pedidos al CEDIS, ventas,
                    conteo físico y ajustes con motivo. Escribirlo directo además
                    dejaba `totalUnits` desfasado (el saldo real del ledger), que es
                    la misma clase de bug que ya nos costó la doble suma. */}
            <ModalConfirmCount
                modal={modalConfirmCount}
                count={countedEntries.length}
                onConfirm={saveCount}
            />
            <ModalRegisterShrinkage
                modal={modalShrinkage}
                branchOfficeId={branchOfficeId}
                inventory={inventory}
                onSaved={() => void getData()}
            />
        </Stack>
    );
};

export default ListInventory;
